import { classDayKey, trustedSummaryDayKeys } from "../util/classDisplay";
import { unwrap } from "../api/scoreApi";

// Shared day-chunked ride-fetch plan for a show (#1906/#1963). GET /ride's
// showId+day filter (#637) backs a one-request-per-DAY plan instead of the
// one-request-per-CLASS anti-pattern: useShowRides (#1906, progressive,
// paints the most recent day first) and useNextRides (#1963, needs the full
// set before it can compute anything, so it awaits every day up front) both
// build their fetch off this same plan instead of each reimplementing the
// dedup.
//
// Returns dayKeys (most-recent-day-first) plus the ids of classes with no
// scheduledTime (classDayKey null -- an emergency/manual-add class, #386):
// no day filter can reach those, so callers fetch them directly by classId.
// A ride with no scheduledTime sitting inside an otherwise-DATED class isn't
// covered by either (#2040) -- see fetchUnscheduledRides below, which callers
// fetch alongside this plan's days/undated classes and merge with
// mergeDedupedRides.
export function planShowRideDays(classes, showSummary) {
  // #3327 -- only a summary stamped with the current dayKeysVersion is
  // trusted; a pre-#1823 row's sliced keys would hide an evening-only day, so
  // it falls through to the class-derived set (classes are already fetched).
  const summaryKeys = trustedSummaryDayKeys(showSummary);
  const dayKeys = summaryKeys
    ? [...summaryKeys].sort().reverse()
    : [...new Set(classes.map(classDayKey).filter(Boolean))].sort().reverse();
  const undatedClassIds = classes.filter((c) => classDayKey(c) == null).map((c) => c.id);
  return { dayKeys, undatedClassIds };
}

// Fetch every undated class's rides directly by classId (one call each --
// there's no day filter to dedup them behind, so this stays proportional to
// the (usually zero, occasionally one or two) undated classes, not the whole
// show). A failed class doesn't sink the rest: caught to [] so one bad
// undated class can't blank the page/derived state.
export function fetchUndatedClassRides(scoreApi, undatedClassIds) {
  return undatedClassIds.length
    ? Promise.all(
        undatedClassIds.map((id) => scoreApi.rides.listRides(id).then(unwrap).catch(() => [])),
      ).then((lists) => lists.flat())
    : Promise.resolve([]);
}

// #2040 — a ride with NO scheduledTime at all is invisible to both of the
// above: the day filter (`fvdStampDayKey(r.scheduledTime) === day`) never
// matches a null scheduledTime, and undatedClassIds only covers classes
// whose EVERY ride is timeless (class.scheduledTime is derived from the
// earliest ride time, daysheet.js ~line 803 -- a class with even one timed
// ride gets a scheduledTime and so is "dated", even if some of its other
// rides are still untimed). A timeless ride sitting inside an otherwise-
// dated class falls through both carve-outs.
//
// GET /ride's `unscheduled=true` (score-api ride.js, card #2040) closes this
// with ONE extra call per show -- not per class -- returning every ride in
// the show with no scheduledTime regardless of which class it's in. In the
// ordinary case that's already a superset of what fetchUndatedClassRides
// above returns (an undated class's rides are, by construction, all
// timeless) -- but that's a rationale for merging rather than a guarantee
// this call always subsumes the other: a stale/null class-row scheduledTime
// could in principle produce a ride this call misses that the classId fetch
// still catches. Callers merge BOTH results and dedupe by ride id
// (mergeDedupedRides, a union keyed on id) rather than relying on either
// list alone, so that edge case can neither drop a ride nor duplicate one.
// Caught to [] so a failure here doesn't blank the rest of the show.
export function fetchUnscheduledRides(scoreApi, showId) {
  return scoreApi.rides
    .listRides(undefined, showId, undefined, undefined, undefined, undefined, undefined, undefined, true)
    .then(unwrap)
    .catch(() => []);
}

// Merge ride lists from the day/undated/unscheduled calls above, deduping by
// ride id (later entries win, though in practice they're identical rows) so
// the #2040 unscheduled-rides call never double-lists a ride that an
// undated-class fetch already returned.
export function mergeDedupedRides(...lists) {
  const byId = new Map();
  for (const list of lists) {
    for (const ride of list) {
      if (ride?.id) byId.set(ride.id, ride);
    }
  }
  return [...byId.values()];
}
